"use client";

import { IntlLink, usePathname } from "@/i18n/navigation";
import { isBodyScrollLocked } from "@/ui/hooks";
import { drawingComplete } from "@/ui/util";


export function ExternalLink(props: Readonly<{
  ref?: React.RefObject<HTMLAnchorElement>;
  href?: string;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  onPointerDown?: React.PointerEventHandler;
  onPointerOut?: React.PointerEventHandler;
  onPointerUp?: React.PointerEventHandler;
  onClick?: React.MouseEventHandler;
}>) {
  return (
    <a
      ref={props.ref}
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      className={props.className}
      onClick={props.onClick}
      title={props.title}
    >
      {props.children}
    </a>
  );
}


export function Link({ onClick, ...props }: React.ComponentProps<typeof IntlLink>) {
  const pathname = usePathname();

  const handleClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(ev);
    const hash = getHrefHash(props, pathname);

    if (hash) {
      ev.preventDefault();
      const scroll = () => scrollTo(hash);

      if (isBodyScrollLocked()) {
        // when there is a modal or mobile menu, wait until it closes
        drawingComplete().then(scroll);
        return;
      }

      // direct scroll
      scroll();
    }
  };

  return (
    <IntlLink
      {...props}
      onClick={handleClick}
    />
  );
}


/**
 * behavior: if ID is an empty string, scroll to top
 */
function scrollTo(hash: Hash): void {
  if (!hash.id) {
    return window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  const element = document.getElementById(hash.id);

  if (element) {
    return element.scrollIntoView({
      behavior: "smooth",
    });
  }
}


type Hash = { id: string | null };
type Pathname = ReturnType<typeof usePathname>;
type LinkProps = Pick<React.ComponentProps<typeof IntlLink>, "href" | "locale" | "as">;

function getHrefHash({ href, ...link }: LinkProps, pathname: Pathname): Hash | null {
  if (!!link.locale) {
    return null;
  }

  if (typeof href === "object" && pathname === href.pathname && !("params" in href)) {
    return {
      id: (
        href.hash
          ? href.hash.startsWith("#") ? href.hash.slice(1) : href.hash
          : null
      ),
    };
  }

  if (typeof href === "string") {
    if (href.startsWith("#") && pathname === link.as) {
      return { id: href.slice(1) };
    }

    if (href === pathname) {
      return { id: null };
    }
  }

  return null;
}
